import { Caption, type SoftColor } from '@if/mui'
import { Box, styled, type Theme } from '@mui/material'

// Scheme-aware palette (CSS vars when the theme has them, static otherwise) so the
// tints follow light/dark — mirrors the design system's own accessor.
const paletteOf = (theme: Theme) => (theme.vars ?? theme).palette

interface TintProps {
  /** Absent when the enum descriptor resolved no color; renders muted, like the zero state. */
  tone?: SoftColor
  /** Tinted when the count is > 0; muted (disabled grey) at zero. */
  on: boolean
}

const forwardTint = (prop: string) => prop !== 'tone' && prop !== 'on'

const Root = styled(Box, { shouldForwardProp: forwardTint })<TintProps>(({ theme, tone, on }) => {
  const palette = paletteOf(theme)
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.25, 0.5),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: on && tone ? palette[tone].background : 'transparent',
    color: on && tone ? palette[tone].main : palette.text.disabled,
  }
})

const Dot = styled(Box, { shouldForwardProp: forwardTint })<TintProps>(({ theme, tone, on }) => ({
  flexShrink: 0,
  width: 6,
  height: 6,
  borderRadius: '50%',
  backgroundColor: on && tone ? paletteOf(theme)[tone].main : paletteOf(theme).text.disabled,
}))

export interface MiniStatProps {
  value: number
  /** Straight from the enum registry, which resolves it to a rating anchor or leaves it unset. */
  color?: SoftColor
  label: string
}

/**
 * One stat inside the Applications cell: a colored dot + count, tinted when the
 * count is > 0 and muted at zero, with the stat name as a hover title. Specific to
 * the Manage Users grid, so it lives here rather than in the shared library.
 */
export const MiniStat = ({ value, color, label }: MiniStatProps) => {
  const on = value > 0
  return (
    <Root tone={color} on={on} title={`${label}: ${value}`}>
      <Dot tone={color} on={on} />
      <Caption sx={{ color: 'inherit', fontVariantNumeric: 'tabular-nums' }}>{value}</Caption>
    </Root>
  )
}
